const EventEmitter = require('events')
const nanoid = require('nanoid/non-secure')
const defer = require('microsistec/defer')

const generateId = (prefix) => prefix + ':' + nanoid()

class Channel {
    /**
     * @param name {String}
     * @param port {MessagePort}
     */
    constructor (name, port) {
        /**
         * @type {String}
         * @private
         */
        this._name = name
        /**
         * @type {MessagePort}
         * @private
         */
        this._port = port
        /**
         * @type {String}
         * @private
         */
        this._id = generateId(name)
        /**
         * @type {Object|null}
         * @private
         */
        this._meta = null

        /**
         * @type {Object.<string, Deferred>}
         * @private
         */
        this._responses = {}

        /**
         * @type {module:events.internal}
         * @private
         */
        this._emitter = new EventEmitter()

        this._port.addEventListener('message', (message) => {
            if (message.data && message.data.__ok) {
                const data = message.data

                switch (data.type) {
                    case 'reply': {
                        const {
                            content, source, requestId, inReplyTo
                        } = data

                        if (source === this._id) {
                            return
                        }

                        const receivedMessage = new ReceivedMessage(
                            'reply',
                            content,
                            this,
                            message,
                            source,
                            requestId
                        )

                        if (this._responses[inReplyTo]) {
                            this._responses[inReplyTo].resolve(receivedMessage)
                        }
                        break
                    }
                    default: {
                        const { type, content, source, requestId } = data

                        if (source === this._id) {
                            return
                        }

                        const receivedMessage = new ReceivedMessage(
                            type,
                            content,
                            this,
                            message,
                            source,
                            requestId
                        )

                        this._emitter.emit('*', { type, message: receivedMessage })
                        this._emitter.emit(type, receivedMessage)
                        break
                    }
                }
            }
        })
    }

    /**
     * @returns {Promise<*>}
     */
    start () {
        this.on('$$booted', (message) => {
            message.reply(true)
        })

        if (this._port.start) {
            this._port.start()
        }

        return this.ask('$$booted').then(() => {
            return undefined
        })
    }

    /**
     * @param eventName {String}
     * @param listener {function(ReceivedMessage)}
     */
    on (eventName, listener) {
        this._emitter.on(eventName, listener)
    }

    /**
     * @param listener {function({type: String, message: ReceivedMessage})}
     */
    onAny (listener) {
        this._emitter.on('*', listener)
    }

    /**
     * @param eventName {String}
     * @param listener {function(ReceivedMessage)}
     */
    off (eventName, listener) {
        this._emitter.off(eventName, listener)
    }

    getName () {
        return this._name
    }

    getId () {
        return this._id
    }

    getPort () {
        return this._port
    }

    setMeta (meta) {
        this._meta = meta
    }

    getMeta () {
        return this._meta
    }

    emit (eventId, content) {
        const requestId = generateId('request')

        console.debug(
            '🔺 %c[%s][%s][%s][%s]%c Message posted %O',
            'font-weight: bold',
            __webpack_hash__,
            this._id,
            requestId,
            eventId,
            'font-weight: normal',
            content
        )

        return this._port.postMessage({
            type: eventId,
            content,
            requestId,
            asking: false,
            __ok: true,
            source: this._id
        })
    }

    ask (eventId, content, timeout = 0) {
        return new Promise((resolve, reject) => {
            const requestId = generateId('request')

            let timeoutId = null

            if (timeout > 0) {
                timeoutId = setTimeout(() => {
                    reject(new Error(`The timeout of ${timeout}ms expired`))
                }, timeout)
            }

            const deferred = defer()

            this._responses[requestId] = deferred

            deferred.then((res) => {
                if (timeoutId) {
                    clearTimeout(timeoutId)
                }

                resolve(res)
            })

            console.debug(
                `❓ %c[%s][%s][%s]%c Asked from ${this._name} %O`,
                'font-weight: bold',
                __webpack_hash__,
                requestId,
                eventId,
                'font-weight: normal',
                content
            )

            this._port.postMessage({
                type: eventId,
                content,
                requestId,
                asking: true,
                __ok: true,
                source: this._id
            })
        })
    }
}

class ReceivedMessage {
    constructor (type, content, channel, event, source, requestId) {
        this.type = type
        this.content = content
        this.channel = channel
        this.event = event
        this.source = source
        this.requestId = requestId
    }

    /**
     * @param content {*}
     */
    reply (content) {
        if (this.channel.getPort() && this.requestId) {
            const requestId = generateId('request')

            console.debug(
                `❗️ %c[%s][%s][%s]%c Replied message ${this.type} to ${this.source} %O`,
                'font-weight: bold',
                __webpack_hash__,
                this.channel.getId(),
                requestId,
                'font-weight: normal',
                content
            )

            this.channel.getPort().postMessage({
                type: 'reply',
                requestId,
                inReplyTo: this.requestId,
                isReply: true,
                content,
                __ok: true,
                source: this._id
            })
        }
    }
}

exports.Channel = Channel
exports.ReceivedMessage = ReceivedMessage

/**
 * @param name {String}
 * @param port {MessagePort}
 */
module.exports = function createChannel (name, port) {
    return new Channel(name, port)
}



// WEBPACK FOOTER //
// ./resources/assets/javascript/sci/std/channel.js